import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Download, RefreshCw } from 'lucide-react';
import { btn } from '../landing/shared';
import { STATUSES, adminApi, errorText } from './adminApi';

const waLink = (phone) => {
  const digits = phone.replace(/\D/g, '');
  if (phone.trim().startsWith('+')) return digits;
  if (digits.startsWith('00')) return digits.slice(2);
  if (digits.startsWith('0')) return `49${digits.slice(1)}`;
  return digits;
};

const fmt = (iso) => new Date(iso).toLocaleString('de-DE', { timeZone: 'Europe/Berlin', dateStyle: 'short', timeStyle: 'short' });

export const ApplicationsTable = ({ onUnauthorized }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleErr = useCallback((err) => {
    if (err.response?.status === 401) onUnauthorized();
    else toast.error(errorText(err));
  }, [onUnauthorized]);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await adminApi().get('/admin/applications')).data); } catch (err) { handleErr(err); }
    setLoading(false);
  }, [handleErr]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    try {
      await adminApi().patch(`/admin/applications/${id}`, { status });
      setRows((r) => r.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success('Status gespeichert');
    } catch (err) { handleErr(err); }
  };

  const exportCsv = async () => {
    try {
      const { data } = await adminApi().get('/admin/applications/export', { responseType: 'blob' });
      const url = URL.createObjectURL(data);
      const a = Object.assign(document.createElement('a'), { href: url, download: 'bewerbungen.csv' });
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { handleErr(err); }
  };

  return (
    <section data-testid="admin-applications">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-2xl font-bold text-navy">Bewerbungen <span className="text-ink" data-testid="admin-applications-count">({rows.length})</span></h2>
        <div className="flex gap-2">
          <button onClick={load} className={btn.outlineDark} data-testid="admin-refresh-btn"><RefreshCw size={16} aria-hidden="true" /> Aktualisieren</button>
          <button onClick={exportCsv} className={btn.primary} data-testid="admin-export-csv-btn"><Download size={16} aria-hidden="true" /> CSV-Export</button>
        </div>
      </div>
      <div className="mt-6 overflow-x-auto rounded-[20px] border border-line bg-white">
        <table className="w-full min-w-[960px] text-left text-sm">
          <thead className="bg-paper text-xs uppercase tracking-wider text-ink">
            <tr>{['Datum', 'Vorname', 'Kontakt', 'Alter', 'Stand', 'Monatlich', 'Ziel', 'Status'].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-line">
            {!loading && rows.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-ink" data-testid="admin-empty">Noch keine Bewerbungen.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="align-top" data-testid={`admin-row-${r.id}`}>
                <td className="whitespace-nowrap px-4 py-3 text-ink">{fmt(r.created_at)}</td>
                <td className="px-4 py-3 font-semibold text-navy">{r.first_name}</td>
                <td className="px-4 py-3">
                  <a href={`mailto:${r.email}`} className="block text-brand hover:underline">{r.email}</a>
                  <a href={`tel:${r.phone.replace(/[^\d+]/g, '')}`} className="block text-navy hover:underline">{r.phone}</a>
                  <a href={`https://wa.me/${waLink(r.phone)}`} target="_blank" rel="noopener noreferrer" className="block text-xs text-brand hover:underline">WhatsApp</a>
                </td>
                <td className="whitespace-nowrap px-4 py-3">{r.age}</td>
                <td className="px-4 py-3">{r.stage}</td>
                <td className="whitespace-nowrap px-4 py-3">{r.budget}</td>
                <td className="max-w-[260px] whitespace-pre-line px-4 py-3 text-ink">{r.goal}</td>
                <td className="px-4 py-3">
                  <select value={r.status} onChange={(e) => changeStatus(r.id, e.target.value)} aria-label={`Status von ${r.first_name}`}
                          className="min-h-[40px] rounded-xl border border-line bg-white px-3 focus:border-brand focus:outline-none" data-testid={`admin-status-select-${r.id}`}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};
